/**
 * Code syntax highlighting themes — palettes complètes (10 thèmes × 10 couleurs).
 *
 * @remarks
 * Repris 1:1 du legacy `sass-front/src/config/codeThemes.ts`. Tree-shakable,
 * hors store Zustand. Consommé par `applyPreferencesToDocument` qui pousse
 * 10 CSS vars `--code-*` au `<html>` à chaque changement de `codeTheme`.
 *
 * Les composants `code/pre` consomment ensuite `var(--code-bg)`, `var(--code-fg)`,
 * etc. — aucune logique JS dans les composants, uniquement la cascade CSS.
 */

export interface CodeTheme {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly colors: {
    readonly background: string;
    readonly foreground: string;
    readonly comment: string;
    readonly string: string;
    readonly number: string;
    readonly keyword: string;
    readonly function: string;
    readonly operator: string;
    readonly punctuation: string;
    readonly variable: string;
  };
}

export const CODE_THEMES: readonly CodeTheme[] = [
  {
    id: 'monokai',
    name: 'Monokai',
    description: 'Classic dark theme with vibrant colors',
    colors: {
      background: '#272822',
      foreground: '#f8f8f2',
      comment: '#75715e',
      string: '#e6db74',
      number: '#ae81ff',
      keyword: '#f92672',
      function: '#a6e22e',
      operator: '#f92672',
      punctuation: '#f8f8f2',
      variable: '#fd971f',
    },
  },
  {
    id: 'dracula',
    name: 'Dracula',
    description: 'Dark theme with a purple accent',
    colors: {
      background: '#282a36',
      foreground: '#f8f8f2',
      comment: '#6272a4',
      string: '#f1fa8c',
      number: '#bd93f9',
      keyword: '#ff79c6',
      function: '#50fa7b',
      operator: '#ff79c6',
      punctuation: '#f8f8f2',
      variable: '#8be9fd',
    },
  },
  {
    id: 'one-dark',
    name: 'One Dark',
    description: "Atom's iconic dark theme",
    colors: {
      background: '#282c34',
      foreground: '#abb2bf',
      comment: '#5c6370',
      string: '#98c379',
      number: '#d19a66',
      keyword: '#c678dd',
      function: '#61afef',
      operator: '#c678dd',
      punctuation: '#abb2bf',
      variable: '#e06c75',
    },
  },
  {
    id: 'github-light',
    name: 'GitHub Light',
    description: 'Clean and minimal light theme',
    colors: {
      background: '#ffffff',
      foreground: '#24292f',
      comment: '#6a737d',
      string: '#032f62',
      number: '#005cc5',
      keyword: '#d73a49',
      function: '#6f42c1',
      operator: '#d73a49',
      punctuation: '#24292f',
      variable: '#e36209',
    },
  },
  {
    id: 'github-dark',
    name: 'GitHub Dark',
    description: "GitHub's dark color scheme",
    colors: {
      background: '#0d1117',
      foreground: '#c9d1d9',
      comment: '#8b949e',
      string: '#a5d6ff',
      number: '#79c0ff',
      keyword: '#ff7b72',
      function: '#d2a8ff',
      operator: '#ff7b72',
      punctuation: '#c9d1d9',
      variable: '#ffa657',
    },
  },
  {
    id: 'nord',
    name: 'Nord',
    description: 'Arctic, north-bluish color palette',
    colors: {
      background: '#2e3440',
      foreground: '#d8dee9',
      comment: '#616e88',
      string: '#a3be8c',
      number: '#b48ead',
      keyword: '#81a1c1',
      function: '#88c0d0',
      operator: '#81a1c1',
      punctuation: '#d8dee9',
      variable: '#d08770',
    },
  },
  {
    id: 'solarized-light',
    name: 'Solarized Light',
    description: 'Precision colors for machines and people',
    colors: {
      background: '#fdf6e3',
      foreground: '#657b83',
      comment: '#93a1a1',
      string: '#2aa198',
      number: '#d33682',
      keyword: '#859900',
      function: '#268bd2',
      operator: '#859900',
      punctuation: '#657b83',
      variable: '#b58900',
    },
  },
  {
    id: 'solarized-dark',
    name: 'Solarized Dark',
    description: 'Precision colors for machines and people',
    colors: {
      background: '#002b36',
      foreground: '#839496',
      comment: '#586e75',
      string: '#2aa198',
      number: '#d33682',
      keyword: '#859900',
      function: '#268bd2',
      operator: '#859900',
      punctuation: '#839496',
      variable: '#b58900',
    },
  },
  {
    id: 'material',
    name: 'Material Theme',
    description: "Google's Material Design inspired",
    colors: {
      background: '#263238',
      foreground: '#eeffff',
      comment: '#546e7a',
      string: '#c3e88d',
      number: '#f78c6c',
      keyword: '#c792ea',
      function: '#82aaff',
      operator: '#89ddff',
      punctuation: '#eeffff',
      variable: '#f07178',
    },
  },
  {
    id: 'tokyo-night',
    name: 'Tokyo Night',
    description: 'Clean dark theme that celebrates the lights of Tokyo',
    colors: {
      background: '#1a1b26',
      foreground: '#a9b1d6',
      comment: '#565f89',
      string: '#9ece6a',
      number: '#ff9e64',
      keyword: '#bb9af7',
      function: '#7aa2f7',
      operator: '#bb9af7',
      punctuation: '#a9b1d6',
      variable: '#f7768e',
    },
  },
];

/**
 * Fallback Monokai (premier élément de CODE_THEMES). Référence stable pour
 * `getCodeTheme` afin d'éviter un non-null assertion sur l'index lookup.
 */
const FALLBACK_CODE_THEME: CodeTheme = CODE_THEMES[0] ?? {
  id: 'monokai',
  name: 'Monokai',
  description: 'Fallback theme',
  colors: {
    background: '#272822',
    foreground: '#f8f8f2',
    comment: '#75715e',
    string: '#e6db74',
    number: '#ae81ff',
    keyword: '#f92672',
    function: '#a6e22e',
    operator: '#f92672',
    punctuation: '#f8f8f2',
    variable: '#fd971f',
  },
};

/**
 * Récupère un thème par id avec fallback sur Monokai si introuvable.
 * Garantit un retour non-null pour simplifier les sites d'appel.
 */
export function getCodeTheme(id: string): CodeTheme {
  return CODE_THEMES.find((t) => t.id === id) ?? FALLBACK_CODE_THEME;
}
